import { AppImage, Icon, SkeletonLoader, TextContent } from '@/components';
import { cn } from '@/utils';
import { TUserRole } from '@/types';
import { USE_MOCK_DATA } from '@/constants';
import React, { useEffect, useState } from 'react';

export interface UserInfoProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * User's display name
   */
  name: string;

  /**
   * User's job title or role
   */
  title: string;

  /**
   * URL to user's image
   */
  imageUrl?: string;

  /**
   * Size of the user's avatar image (default: 80px)
   */
  imageSize?: number;

  /**
   * Loading state indicator
   */
  isLoading?: boolean;

  /**
   * Text side
   */
  textSide?: 'left' | 'right';

  /**
   * Text alignment
   */
  textAlignment?: 'left' | 'right';

  /**
   * Dictates how the content aligns within the component
   */
  contentAlignment?: 'left' | 'center' | 'right';

  /**
   * Context of the user info, used for analytics or conditional styling
   */
  context?: TUserRole;
}

/**
 * UserInfo Component
 *
 * A simple user information component that displays a user's name, title,
 * and avatar in a clean, rounded card layout.
 */
export const UserInfo: React.FC<UserInfoProps> = ({
  name,
  title,
  imageUrl: initialImageUrl,
  imageSize = 80,
  isLoading = false,
  textSide = 'right',
  textAlignment = 'left',
  contentAlignment = 'left',
  className,
  context = 'Sales Representative',
  ...props
}) => {
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [useFallbackImage, setUseFallbackImage] = useState(false);

  useEffect(() => {
    setImageUrl(initialImageUrl);
  }, [initialImageUrl]);

  // Load image from localStorage on component mount
  useEffect(() => {
    const windowExists = typeof window !== 'undefined';
    const savedImageDataUrl = windowExists ? localStorage.getItem('savedImage') : null;
    if (savedImageDataUrl && context === 'Sales Representative') {
      setImageUrl(savedImageDataUrl);
    }
  }, [context]);

  // Convert incoming image URL to data URL and store in localStorage
  useEffect(() => {
    let isCancelled = false;

    const persistImage = async () => {
      if (context !== 'Sales Representative') return; // Only persist images in the sales rep context
      if (!initialImageUrl || initialImageUrl === '' || typeof window === 'undefined') return;
      if (localStorage.getItem('savedImage')) return; // Don't overwrite if we already have a saved image

      // If the incoming image URL is the same as the saved one, no need to re-fetch and convert
      const savedImageDataUrl = localStorage.getItem('savedImage');
      if (initialImageUrl === savedImageDataUrl) return;

      try {
        const response = await fetch(initialImageUrl);
        const blob = await response.blob();

        const reader = new FileReader();
        reader.onloadend = () => {
          if (isCancelled) return;
          const dataUrl = reader.result as string;
          localStorage.setItem('savedImage', dataUrl);
          setImageUrl(dataUrl);
        };
        reader.readAsDataURL(blob);
      } catch {
        // Fail silently to avoid breaking UI
        setUseFallbackImage(true);
      }
    };

    persistImage();

    return () => {
      isCancelled = true;
    };
  }, [imageUrl, initialImageUrl, context]);

  // Container alignment classes
  const contentAlignmentClassNames = cn(
    'flex',
    'w-full',
    contentAlignment === 'left' && 'justify-start',
    contentAlignment === 'center' && 'justify-center',
    contentAlignment === 'right' && 'justify-end',
  );

  // Main class names based on text side
  const componentClassNames = cn(
    'flex items-center gap-5',
    textSide === 'left' ? 'flex-row-reverse text-right' : 'flex-row',
    textAlignment === 'left' && 'text-left',
    textAlignment === 'right' && 'text-right',
    className,
  );

  return (
    <div className={contentAlignmentClassNames}>
      <div className={componentClassNames} {...props}>
        {/* Avatar */}
        <div
          className="flex-shrink-0 overflow-hidden rounded-full"
          style={{ width: imageSize, height: imageSize }}
        >
          {isLoading ? (
            <>
              <SkeletonLoader rounded="full" style={{ width: imageSize, height: imageSize }} />
            </>
          ) : (
            <>
              {imageUrl ? (
                <AppImage
                  src={imageUrl}
                  alt={`${name}'s avatar`}
                  className="h-full w-full object-cover"
                  height={imageSize}
                  width={imageSize}
                  fallbackSrc={USE_MOCK_DATA ? '/public/user-profiles/TKim45.jpg' : initialImageUrl}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-neutral-gray-300">
                  <Icon name="User" size={imageSize / 2} className="text-neutral-gray-500" />
                </div>
              )}
            </>
          )}
        </div>

        {/* User Details */}
        <div className="min-w-0 flex-1">
          <TextContent
            className="break-words"
            isLoading={isLoading}
            label={name}
            level={3}
            loadingClassName="h-6 w-24"
            type="headline"
            variant="sectionSubTitle"
          />
          <TextContent
            className="truncate"
            color="neutral-gray-700"
            isLoading={isLoading}
            label={title}
            loadingClassName="h-4 mt-1 w-36"
            type="paragraph"
            variant="bodyMedium"
          />
        </div>
      </div>
    </div>
  );
};

UserInfo.displayName = 'UserInfo';

export default UserInfo;
